import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, resolveDynamicComponent as _resolveDynamicComponent, openBlock as _openBlock, createBlock as _createBlock, resolveComponent as _resolveComponent, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "bg-white mx-auto pt-6" }
const _hoisted_2 = { class: "flex item-center justify-between mt-6" }
const _hoisted_3 = { class: "flex" }
const _hoisted_4 = { key: 0 }
const _hoisted_5 = { key: 1 }
const _hoisted_6 = { class: "p-8 relative" }
const _hoisted_7 = { class: "mx-auto flex items-center justify-center" }
const _hoisted_8 = { class: "mt-6 text-center" }
const _hoisted_9 = { class: "text-lg font-semibold leading-6 text-gray-900" }
const _hoisted_10 = { class: "mt-4 text-center" }
const _hoisted_11 = { class: "text-sm text-gray-600 mb-2" }
const _hoisted_12 = { class: "text-gray-600" }
const _hoisted_13 = /*#__PURE__*/_createElementVNode("br", null, null, -1)
const _hoisted_14 = /*#__PURE__*/_createElementVNode("br", null, null, -1)
const _hoisted_15 = { class: "mt-6 flex justify-around items-center gap-6" }
const _hoisted_16 = { class: "p-8 relative" }
const _hoisted_17 = { class: "mx-auto flex items-center justify-center" }
const _hoisted_18 = { class: "mt-6 text-center" }
const _hoisted_19 = { class: "text-lg font-semibold leading-6 text-gray-900" }
const _hoisted_20 = { class: "mt-4 text-center" }
const _hoisted_21 = { class: "text-sm text-gray-600" }
const _hoisted_22 = { class: "mt-6 flex justify-around items-center gap-6" }

import { union } from "lodash";
import { ref, onBeforeMount, computed } from "vue";

import PageHeaderComponent from "@/components/global/PageHeaderComponent.vue";
import { useSchoolStore } from "@/stores/schoolStore";
import { useLearnerStore } from "@/stores/learnerStore";
import { useRolloverStore } from "@/stores/rolloverStore";
import { Rollover } from "@/interfaces/rollover.interface";
import LearnersRolloverTableComponent from "@/modules/main/components/LearnersRolloverTableComponent.vue";
import { useRouter } from "vue-router";
import TutorialModalComponent from "../components/TutorialModalComponent.vue";

type LearnersRolloverTableInstance = InstanceType<
  typeof LearnersRolloverTableComponent
>;

// Data stores

export default /*#__PURE__*/_defineComponent({
  __name: 'IncompleteRolloverView',
  setup(__props) {

const router = useRouter();

// Types or Interfaces
const schoolStore = useSchoolStore();
const learnerStore = useLearnerStore(); 
const rolloverStore = useRolloverStore();

// Local State: Read & Update
const showModal = ref(false);
const isGoingBack = ref(false);
const currentGradeIndex = ref(0);
const allGradeLevels = ref<any>([]);
const confirmationModal = ref(false);
const goToClassReviewModal = ref(false);
const learnersRolloverTable = ref<LearnersRolloverTableInstance | null>(null);

// Local Reactive Variables: Usually for Read Only
const currentGrade = computed(
  () => allGradeLevels.value[currentGradeIndex.value]
);

const allLearners = computed(() => learnerStore.getSchoolLearners.length);
const learnersLeaving = computed(() => rolloverStore.rollover?.learnerActions.leaving.length || 0);
const learnersRepeating = computed(() => rolloverStore.rollover?.learnerActions.repeating.length || 0);

const advancingLearners = computed(
  () => allLearners.value - (learnersLeaving.value + learnersRepeating.value)
);

// Things to do before the component is mounted
onBeforeMount(async () => {
  allGradeLevels.value = schoolStore.currentSchoolStructure?.gradeLevels.sort((a, b) => a - b);

  const currentRollOver = rolloverStore.rollover as Rollover;

  if (currentRollOver && currentRollOver.status.learnerRollover === "to_do") {
    currentRollOver.status.learnerRollover = "in_review";
    await rolloverStore.updateRolloverObject(currentRollOver);
  }
});

// save progress button logic
const saveProgress = async () => {
  console.log("Saving Progress: ")
  if (learnersRolloverTable.value) {
    const learnersToRemove = learnersRolloverTable.value.learnersToRemove;
    const learnersToRepeat = learnersRolloverTable.value.learnersToRepeat;
    const currentRollObject = rolloverStore.rollover as Rollover;

    const currentGradeLearnerIds = learnerStore.schoolLearners
      .filter(l => l.grade == currentGrade.value)
      .map(l => l._id);

    // For learners leaving:
    // Remove any current grade learner IDs from the overall 'leaving' list
    currentRollObject.learnerActions.leaving = currentRollObject.learnerActions.leaving.filter(
      (id) => !currentGradeLearnerIds.includes(id)
    )

    // Now add the current grade's selections
    currentRollObject.learnerActions.leaving = union(
      currentRollObject.learnerActions.leaving,
      learnersToRemove
    );

    // For learners repeating:
    // Remove any current grade learner IDs from the overall 'repeating' list
    currentRollObject.learnerActions.repeating = currentRollObject.learnerActions.repeating.filter(
      (id) => !currentGradeLearnerIds.includes(id)
    );
    // Now add the current grade's selections
    currentRollObject.learnerActions.repeating = union(
      currentRollObject.learnerActions.repeating,
      learnersToRepeat
    );

    await rolloverStore.updateRolloverObject(currentRollObject);
  }
};

const confirmLearnerRolloverDetails = async () => {
  const currentRollObject = rolloverStore.rollover as Rollover;

  currentRollObject.status.learnerRollover = "pending_job_run";
  await rolloverStore.updateRolloverObject(currentRollObject);

  confirmationModal.value = false;
  goToClassReviewModal.value = true;
};

// Function to navigate to the next grade level
const nextGrade = async () => {
  await saveProgress();
  isGoingBack.value = false;

  if (
    currentGrade.value < allGradeLevels.value[allGradeLevels.value.length - 1]
  ) {
    currentGradeIndex.value++;
  } else if (
    currentGrade.value === allGradeLevels.value[allGradeLevels.value.length - 1]
  ) {
    confirmationModal.value = true;
  }
};

const previousGrade = async () => {
  await saveProgress();

  if (currentGradeIndex.value > 0) {
    isGoingBack.value = true;
    currentGradeIndex.value--;
  }
};

const goBack = () => {
  goToClassReviewModal.value = false;
};

const goToClassReview = async () => {
  const currentRollObject = rolloverStore.rollover as Rollover;

  if (currentRollObject && currentRollObject.status.classReview === "to_do") {
    currentRollObject.status.classReview = "in_review";
    await rolloverStore.updateRolloverObject(currentRollObject);
  }

  goToClassReviewModal.value = false;
  router.push("/main/review-class-structure");
};

return (_ctx: any,_cache: any) => {
  const _component_rl_icon = _resolveComponent("rl-icon")!
  const _component_router_link = _resolveComponent("router-link")!
  const _component_modal = _resolveComponent("modal")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(PageHeaderComponent, { pageHeading: 'Rollover' }),
    _createElementVNode("div", null, [
      _createElementVNode("div", _hoisted_1, [
        (_openBlock(), _createBlock(_resolveDynamicComponent(LearnersRolloverTableComponent), {
          key: currentGrade.value,
          gradeLevel: currentGrade.value,
          ref_key: "learnersRolloverTable",
          ref: learnersRolloverTable,
          isGoingBack: isGoingBack.value
        }, null, 8, ["gradeLevel", "isGoingBack"])),
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_router_link, { to: "/main/internal-edit-school" }, {
            default: _withCtx(() => [
              _createElementVNode("button", {
                onClick: _cache[0] || (_cache[0] = ($event: any) => (saveProgress())),
                class: "px-4 p-2 justify-center font-semibold text-gray-700 text-base border-gray-300 border rounded-lg font-medium flex items-center bg-transparent shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] text-center bg-whitemax-w-[95px]"
              }, [
                _createVNode(_component_rl_icon, {
                  icon: 'rl-small-back-arrow',
                  title: 'rl small back arrow',
                  "aria-hidden": "true"
                }),
                _createTextVNode(" " + _toDisplayString(_ctx.$t("rollover.incomplete_view_screen.back")), 1)
              ])
            ]),
            _: 1
          }),
          _createElementVNode("div", _hoisted_3, [
            (currentGradeIndex.value > 0)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  class: "px-4 p-2 justify-center font-semibold text-gray-700 text-base border-gray-300 border rounded-lg font-medium flex items-center bg-transparent shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] text-center bg-whitemax-w-[95px] mr-2",
                  onClick: _cache[1] || (_cache[1] = ($event: any) => (previousGrade()))
                }, _toDisplayString(_ctx.$t("rollover.incomplete_view_screen.previous")), 1))
              : _createCommentVNode("", true),
            _createElementVNode("button", {
              class: "px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center justify-center cursor-pointer",
              onClick: _cache[2] || (_cache[2] = ($event: any) => (nextGrade()))
            }, [
              (currentGrade.value < allGradeLevels.value[allGradeLevels.value.length - 1])
                ? (_openBlock(), _createElementBlock("span", _hoisted_4, _toDisplayString(_ctx.$t("rollover.incomplete_view_screen.confirm_review_next_grade")), 1))
                : (_openBlock(), _createElementBlock("span", _hoisted_5, _toDisplayString(_ctx.$t("rollover.incomplete_view_screen.confirm_rollover")) + " " + _toDisplayString(advancingLearners.value) + " " + _toDisplayString(_ctx.$t("rollover.incomplete_view_screen.students")), 1))
            ])
          ])
        ])
      ]),
      _createVNode(_component_modal, {
        showCloseButton: true,
        widthClass: 'max-w-xl',
        open: confirmationModal.value,
        onLocalModalOpen: _cache[5] || (_cache[5] = ($event: any) => (confirmationModal.value = $event))
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("div", null, [
              _createElementVNode("div", _hoisted_7, [
                _createVNode(_component_rl_icon, {
                  icon: 'rl-magnifying-glass',
                  title: 'rl magnifying glass',
                  "aria-hidden": "true"
                })
              ]),
              _createElementVNode("div", _hoisted_8, [
                _createElementVNode("h3", _hoisted_9, _toDisplayString(_ctx.$t("rollover.modal.incomplete_view.confirm_selection")), 1)
              ]),
              _createElementVNode("div", _hoisted_10, [
                _createElementVNode("p", _hoisted_11, _toDisplayString(_ctx.$t("rollover.modal.incomplete_view.you_selected")) + ": ", 1),
                _createElementVNode("p", _hoisted_12, [
                  _createTextVNode(_toDisplayString(advancingLearners.value) + " " + _toDisplayString(_ctx.$t("rollover.modal.incomplete_view.students_to_move")) + " " + _toDisplayString(_ctx.$t("global.grade_naming_convention")), 1),
                  _hoisted_13,
                  _createTextVNode(" " + _toDisplayString(learnersRepeating.value) + " " + _toDisplayString(_ctx.$t("rollover.modal.incomplete_view.students_to_remain")) + " " + _toDisplayString(_ctx.$t("global.grade_naming_convention")), 1),
                  _hoisted_14,
                  _createTextVNode(" " + _toDisplayString(learnersLeaving.value) + " " + _toDisplayString(_ctx.$t("rollover.modal.incomplete_view.students_to_remove")), 1)
                ])
              ])
            ]),
            _createElementVNode("div", _hoisted_15, [
              _createElementVNode("button", {
                type: "button",
                class: "text-gray-700 border border-gray-300 rounded-lg p-2.5 text-base flex flex-row justify-center items-center flex-wrap w-1/2",
                onClick: _cache[3] || (_cache[3] = ($event: any) => (confirmationModal.value = false))
              }, _toDisplayString(_ctx.$t("rollover.modal.incomplete_view.edit")), 1),
              _createElementVNode("button", {
                type: "button",
                class: "bg-blue-600 rounded-lg p-2.5 text-base shadow-lg text-white flex flex-row justify-center items-center flex-wrap w-1/2",
                onClick: _cache[4] || (_cache[4] = ($event: any) => (confirmLearnerRolloverDetails()))
              }, _toDisplayString(_ctx.$t("rollover.modal.incomplete_view.correct")), 1)
            ])
          ])
        ]),
        _: 1
      }, 8, ["open"]),
      _createVNode(_component_modal, {
        showCloseButton: true,
        widthClass: 'max-w-xl',
        open: goToClassReviewModal.value,
        onLocalModalOpen: _cache[8] || (_cache[8] = ($event: any) => (goToClassReviewModal.value = $event))
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_16, [
            _createElementVNode("div", null, [
              _createElementVNode("div", _hoisted_17, [
                _createVNode(_component_rl_icon, {
                  icon: 'rl-team-collaboration',
                  title: 'rl team collaboration',
                  "aria-hidden": "true"
                })
              ]),
              _createElementVNode("div", _hoisted_18, [
                _createElementVNode("h3", _hoisted_19, _toDisplayString(_ctx.$t("rollover.modal.incomplete_view.review_structure")), 1)
              ]),
              _createElementVNode("div", _hoisted_20, [
                _createElementVNode("p", _hoisted_21, _toDisplayString(_ctx.$t("rollover.modal.incomplete_view.review_text")) + " " + _toDisplayString(_ctx.$t("global.grade_naming_convention")) + ". ", 1)
              ])
            ]),
            _createElementVNode("div", _hoisted_22, [
              _createVNode(_component_router_link, {
                to: "/main/overview",
                class: "w-1/2"
              }, {
                default: _withCtx(() => [
                  _createElementVNode("button", {
                    type: "button",
                    class: "text-gray-700 border border-gray-300 rounded-lg p-2.5 rounded-full px-8 py-2 text-base mx-2 flex flex-row justify-center items-center flex-wrap w-full",
                    onClick: _cache[6] || (_cache[6] = ($event: any) => (goBack()))
                  }, _toDisplayString(_ctx.$t("rollover.modal.incomplete_view.do_later")), 1)
                ]),
                _: 1
              }),
              _createElementVNode("button", {
                onClick: _cache[7] || (_cache[7] = ($event: any) => (goToClassReview())),
                type: "button",
                class: "bg-blue-600 rounded-lg p-2.5 text-base shadow-lg text-white flex flex-row justify-center items-center flex-wrap w-1/2"
              }, _toDisplayString(_ctx.$t("rollover.modal.incomplete_view.review_now")), 1)
            ])
          ])
        ]),
        _: 1
      }, 8, ["open"])
    ]),
    _createVNode(TutorialModalComponent, {
      show: showModal.value,
      "onUpdate:show": _cache[9] || (_cache[9] = ($event: any) => ((showModal).value = $event))
    }, {
      headerSlot: _withCtx(() => [
        _createTextVNode(" Tutorial ")
      ]),
      descriptionSlot: _withCtx(() => [
        _createTextVNode(" Lorem ipsum dolor sit amet consectetur adipisicing elit. Accusamus commodi, suscipit, unde deleniti distinctio qui culpa vero repudiandae eum provident laudantium officia laboriosam harum iste fugit nobis minus, consectetur dolorem. ")
      ]),
      strapiContent: _withCtx(() => [
        _createTextVNode(" Strapi content to go here... ")
      ]),
      _: 1
    }, 8, ["show"])
  ], 64))
}
}

})